import React, { useState } from 'react';
import { Button, Input, Typography, Checkbox, theme } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconMail } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import HeroBanner, { HeroBannerImage } from '../../components/HeroBanner';
import { createSignupLead } from '../../services/SiteModeService';
import { SIGNUP_LEAD_INTERESTS } from '../../interfaces/heavyDutyMachinery/ISiteMode';
import { SIGNUP_LEAD_INTEREST_LABEL_KEYS } from '../../constants/signupLeadInterests';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, SURFACE_1 } from '../../theme/creamyTokens';
import heroWebp from '../../assets/hero/worksite-night.webp';
import heroWebpMobile from '../../assets/hero/worksite-night-mobile.webp';
import heroJpg from '../../assets/hero/worksite-night.jpg';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const HERO_IMAGE: HeroBannerImage = { webp: heroWebp, webpMobile: heroWebpMobile, fallback: heroJpg };

/**
 * What a stranger sees while the platform is not open yet.
 *
 * Its whole job is to turn interest that would otherwise evaporate --
 * somebody typing the domain into a phone at a coffee, a name passed along
 * in a trade where everything travels by word of mouth -- into a row in a
 * table. The platform is weeks from letting anybody register and that
 * interest does not wait.
 *
 * The one question that earns its place is what they are here FOR. Every
 * conversation so far has been with people who OWN machinery; there is not
 * a single data point about anybody wanting to rent one. These four
 * checkboxes are the first place that could show up, which is why they are
 * multiple choice and not a dropdown nobody reads.
 *
 * Reuses the app's own hero band rather than inventing a second look: the
 * people who see this page today are the same people who will see the real
 * one in a few weeks, and a landing that looks like a different product
 * wastes the recognition.
 */
const PreLaunchLanding: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [messageText, setMessageText] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  // The honeypot's own state. Hidden from humans, so anything that arrives
  // here was typed by something that reads the DOM rather than the page.
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const consentText = translate(t, TRANSLATIONS.PRELAUNCH_CONSENT_LABEL);

  const toggleInterest = (value: string) =>
    setInterests((prev) => (prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]));

  const handleSubmit = async () => {
    if (!email.trim()) {
      message.error(translate(t, TRANSLATIONS.PRELAUNCH_EMAIL_REQUIRED));
      return;
    }
    if (interests.length === 0) {
      message.error(translate(t, TRANSLATIONS.PRELAUNCH_INTEREST_REQUIRED));
      return;
    }
    if (!consent) {
      message.error(translate(t, TRANSLATIONS.PRELAUNCH_CONSENT_REQUIRED));
      return;
    }

    setSubmitting(true);
    try {
      await createSignupLead({
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: messageText.trim() || undefined,
        interests,
        consentGiven: true,
        // Sent back exactly as rendered, in the language they read it in --
        // what gets stored has to be what was on screen, not what the
        // server would render today.
        consentText,
        website,
      });
      setDone(true);
    } catch {
      message.error(translate(t, TRANSLATIONS.PRELAUNCH_SUBMIT_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const pillInput: React.CSSProperties = { borderRadius: 999, height: 46 };

  return (
    <div style={{ minHeight: '100vh', background: SURFACE_1_SOFT }}>
      <HeroBanner image={HERO_IMAGE} height={isMobile ? 380 : 460} focalPoint="50% 45%" pullUpTop={0} contentPadding={isMobile ? '0 20px' : '0 56px'}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <span
            style={{
              display: 'inline-block',
              padding: '6px 14px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.16)',
              backdropFilter: 'blur(6px)',
              color: '#fff',
              fontSize: 12,
              letterSpacing: 1,
              textTransform: 'uppercase',
              marginBottom: 16,
            }}
          >
            {translate(t, TRANSLATIONS.PRELAUNCH_BADGE)}
          </span>
          <Title level={1} style={{ color: '#fff', margin: 0, fontSize: isMobile ? 32 : 46, lineHeight: 1.1, textWrap: 'balance' }}>
            {translate(t, TRANSLATIONS.PRELAUNCH_HERO_TITLE)}
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.92)', fontSize: isMobile ? 16 : 19, marginTop: 14, marginBottom: 0 }}>
            {translate(t, TRANSLATIONS.PRELAUNCH_HERO_SUBTITLE)}
          </Paragraph>
        </div>
      </HeroBanner>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: isMobile ? '28px 20px 56px' : '40px 20px 72px' }}>
        {done ? (
          // Deliberately replaces the form rather than sitting above it: the
          // job is done, and leaving the fields there invites a second
          // submission that helps nobody.
          <div style={{ background: TEAL_SOFT_BG, borderRadius: 20, padding: isMobile ? 24 : 32, textAlign: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: TEAL,
                color: '#fff',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
              }}
            >
              <IconCheck size={26} />
            </span>
            <Title level={4} style={{ margin: 0 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_DONE_TITLE)}
            </Title>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_DONE_BODY)}
            </Paragraph>
          </div>
        ) : (
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: 20,
              padding: isMobile ? 22 : 30,
              boxShadow: '0 1px 2px rgba(22,33,28,.05), 0 10px 30px rgba(22,33,28,.06)',
            }}
          >
            <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_FORM_TITLE)}
            </Title>
            <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 20 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_FORM_SUBTITLE)}
            </Text>

            <Text style={{ display: 'block', fontSize: 13, marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_INTEREST_LABEL)}
            </Text>
            {/* Pills rather than a list of checkboxes: four short answers a
                thumb can hit, and picking several has to look as normal as
                picking one -- because for half these people it is. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {SIGNUP_LEAD_INTERESTS.map((value) => {
                const selected = interests.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleInterest(value)}
                    style={{
                      border: `1px solid ${selected ? TEAL : token.colorBorder}`,
                      background: selected ? TEAL_SOFT_BG : 'transparent',
                      color: selected ? TEAL : token.colorText,
                      borderRadius: 999,
                      padding: '9px 16px',
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'background-color .15s ease, border-color .15s ease',
                    }}
                  >
                    {translate(t, SIGNUP_LEAD_INTEREST_LABEL_KEYS[value])}
                  </button>
                );
              })}
            </div>

            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_EMAIL_LABEL)}
            </Text>
            <Input
              id="prelaunch-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              prefix={<IconMail size={16} style={{ color: token.colorTextTertiary }} />}
              style={{ ...pillInput, marginBottom: 14 }}
            />

            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_PHONE_LABEL)}
            </Text>
            <Input
              id="prelaunch-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ ...pillInput, marginBottom: 14 }}
            />

            <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_MESSAGE_LABEL)}
            </Text>
            <TextArea
              id="prelaunch-message"
              rows={3}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={translate(t, TRANSLATIONS.PRELAUNCH_MESSAGE_PLACEHOLDER)}
              maxLength={2000}
              style={{ borderRadius: 16, marginBottom: 18 }}
            />

            {/* The honeypot. Hidden from people and from screen readers, and
                left out of the tab order, so nobody using the page can reach
                it by accident -- only something filling in every input it
                finds. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="prelaunch-website">Website</label>
              <input id="prelaunch-website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <div style={{ background: SURFACE_1, borderRadius: 14, padding: '12px 14px', marginBottom: 18 }}>
              <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)}>
                <span style={{ fontSize: 13 }}>
                  {consentText}{' '}
                  <Link to="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: TEAL }}>
                    {translate(t, TRANSLATIONS.PRELAUNCH_PRIVACY_LINK)}
                  </Link>
                </span>
              </Checkbox>
            </div>

            <Button
              type="primary"
              block
              size="large"
              loading={submitting}
              onClick={handleSubmit}
              style={{ borderRadius: 999, height: 50, fontSize: 16 }}
            >
              {translate(t, TRANSLATIONS.PRELAUNCH_SUBMIT)}
            </Button>

            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12, textAlign: 'center' }}>
              {translate(t, TRANSLATIONS.PRELAUNCH_NO_SPAM)}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreLaunchLanding;
